import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Download, Loader2, Plus, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { useSessionState } from '@/hooks/use-session-state';
import { AccountModal } from './account-modal';
import { BulkActionBar } from './bulk-action-bar';
import { ChangeLogPanel } from './change-log-panel';
import { ChartHealthPanel, type HealthIssueKey } from './chart-health-panel';
import { TravelExportCoaDialog } from './export-coa-dialog';
import { ImportAccountsDialog } from './import-accounts-dialog';
import { ReparentDialog } from './reparent-dialog';
import { WorkbenchTreeRow, type EditableField, type WorkbenchNode } from './workbench-tree-row';
import {
  collectCodes,
  filterTree,
  flattenTree,
  isInvalidDrop,
  nextSelection,
  withSubtreeBalances,
} from './workbench-model';

/**
 * Chart-of-accounts workbench.
 *
 * Replaces a read-only tree that navigated away on every click. The accountant
 * edited 428 accounts in ten days through that screen; this one keeps them
 * inside the tree — inline edits, multi-select bulk actions, drag to re-parent,
 * a health worklist, and session undo.
 */

const TYPE_COLORS: Record<string, string> = {
  ASSET: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  LIABILITY: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  EQUITY: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  INCOME: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  EXPENSE: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
};

export function ChartWorkbench() {
  const { t } = useTranslation('accounting');
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [search, setSearch] = useSessionState('travel:accounting:searchQuery', '');
  const [typeFilter, setTypeFilter] = useSessionState<string>('travel:accounting:typeFilter', '');
  const [showInactive, setShowInactive] = useSessionState('travel:accounting:showInactive', false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lastClickedId, setLastClickedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; field: EditableField } | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [healthFilter, setHealthFilter] = useState<{
    key: HealthIssueKey | null;
    ids: Set<string> | null;
  }>({ key: null, ids: null });
  const [rowWarnings, setRowWarnings] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [createUnder, setCreateUnder] = useState<WorkbenchNode | null>(null);
  const [pendingDrop, setPendingDrop] = useState<{ dragged: WorkbenchNode; target: WorkbenchNode } | null>(
    null
  );

  const { data: chartPrep } = trpc.account.chartPrepStatus.useQuery();
  const unlocked = chartPrep?.enabled === true;

  // Inactive accounts were unreachable: the query hardcoded isActive: true, so
  // deactivating an account hid it permanently.
  const { data: treeData, isLoading } = trpc.account.getTree.useQuery(
    showInactive ? {} : { isActive: true }
  );
  const tree = useMemo(
    () => withSubtreeBalances((treeData ?? []) as unknown as WorkbenchNode[]),
    [treeData]
  );

  const update = trpc.account.update.useMutation({
    onMutate: ({ id }) => setSavingId(id),
    onSettled: () => setSavingId(null),
    onSuccess: (result) => {
      utils.account.getTree.invalidate();
      utils.account.list.invalidate();
      utils.accountWorkbench.health.invalidate();
      setEditing(null);
      setRowWarnings(result.warnings ?? []);
    },
    onError: (error) => {
      setEditing(null);
      setRowWarnings([error.message]);
    },
  });

  const visible = useMemo(() => {
    let rows = tree;
    if (typeFilter) rows = rows.filter((node) => node.type === typeFilter);
    if (search.trim()) rows = filterTree(rows, search.trim());
    return rows;
  }, [tree, typeFilter, search]);

  const flat = useMemo(() => flattenTree(visible), [visible]);
  // Searching or filtering by health should reveal matches, not hide them in
  // collapsed branches.
  const effectiveExpanded =
    search.trim() || healthFilter.ids ? collectCodes(visible) : expanded;

  const toggleSelect = (id: string, shiftKey: boolean) => {
    setSelected((previous) => nextSelection(previous, flat, id, { shiftKey, anchorId: lastClickedId }));
    setLastClickedId(id);
  };

  const commit = (id: string, field: EditableField, value: string) => {
    const node = flat.find((candidate) => candidate.id === id);
    if (!node || value === String(node[field as keyof WorkbenchNode] ?? '')) {
      setEditing(null);
      return;
    }
    update.mutate({ id, [field]: value } as Parameters<typeof update.mutate>[0]);
  };

  const allSelected = flat.length > 0 && flat.every((node) => selected.has(node.id));

  return (
    <div className="space-y-3">
      {unlocked && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            {t('chartPrep.title')}
          </p>
          <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">{t('chartPrep.body')}</p>
        </div>
      )}

      <ChartHealthPanel
        activeKey={healthFilter.key}
        onSelect={(key, ids) =>
          setHealthFilter({ key, ids: key ? new Set(ids) : null })
        }
      />

      <ChangeLogPanel />

      <BulkActionBar
        selectedIds={[...selected]}
        onClear={() => setSelected(new Set())}
        onDone={() => setSelected(new Set())}
        onDeleted={(ids) =>
          setSelected((previous) => {
            const next = new Set(previous);
            for (const id of ids) next.delete(id);
            return next;
          })
        }
      />

      {rowWarnings.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 px-4 py-2 dark:border-amber-700 dark:bg-amber-950/30">
          <ul className="list-inside list-disc space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
            {rowWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
          <button
            className="mt-1.5 text-xs font-medium text-amber-900 underline dark:text-amber-200"
            onClick={() => setRowWarnings([])}
          >
            {t('accountModal.acknowledge')}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder={t('chartOfAccounts.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full max-w-xs border px-3 text-sm focus:outline-none focus:ring-1"
          />
          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            className="border-border bg-background h-8 border px-2 text-sm"
          >
            <option value="">{t('chartOfAccounts.allTypes')}</option>
            <option value="ASSET">{t('chartOfAccounts.assets')}</option>
            <option value="LIABILITY">{t('chartOfAccounts.liabilities')}</option>
            <option value="EQUITY">{t('chartOfAccounts.equity')}</option>
            <option value="INCOME">{t('chartOfAccounts.income')}</option>
            <option value="EXPENSE">{t('chartOfAccounts.expenses')}</option>
          </select>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(value) => setShowInactive(value === true)}
            />
            {t('workbench.showInactive')}
          </label>
          <div className="flex gap-1">
            <button
              onClick={() => setExpanded(collectCodes(tree))}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded px-2 py-1 text-xs"
            >
              {t('workbench.expandAll')}
            </button>
            <button
              onClick={() => setExpanded(new Set())}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded px-2 py-1 text-xs"
            >
              {t('workbench.collapseAll')}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unlocked && (
            <Button variant="outline" className="h-8" onClick={() => setImportOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              {t('import.button')}
            </Button>
          )}
          <Button variant="outline" className="h-8" onClick={() => setExportOpen(true)}>
            <Download className="mr-2 h-4 w-4" />
            {t('chartOfAccounts.export')}
          </Button>
          <Button
            className="h-8"
            onClick={() => {
              setCreateUnder(null);
              setModalOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('chartOfAccounts.newAccount')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="border-border bg-card border py-12 text-center">
          <BookOpen className="text-muted-foreground/50 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('chartOfAccounts.noAccountsFound')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('chartOfAccounts.adjustFilters')}
          </p>
        </div>
      ) : (
        <div className="border-border bg-card overflow-x-auto border">
          <table className="w-full min-w-[860px]">
            <thead className="bg-muted/50 border-border border-b">
              <tr>
                <th className="w-8 px-2 py-2">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(value) =>
                      setSelected(value === true ? new Set(flat.map((n) => n.id)) : new Set())
                    }
                    aria-label={t('workbench.selectAll')}
                  />
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium">
                  {t('chartOfAccounts.code')}
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium">
                  {t('chartOfAccounts.name')}
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium">
                  {t('chartOfAccounts.type')}
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium">
                  {t('chartOfAccounts.balance')}
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium">
                  {t('workbench.postings')}
                </th>
                <th className="w-16 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {visible.map((node) => (
                <WorkbenchTreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  expanded={effectiveExpanded}
                  selected={selected}
                  visibleIds={healthFilter.ids}
                  typeColors={TYPE_COLORS}
                  editing={editing}
                  onEdit={setEditing}
                  savingId={savingId}
                  onToggleExpand={(code) =>
                    setExpanded((previous) => {
                      const next = new Set(previous);
                      if (next.has(code)) next.delete(code);
                      else next.add(code);
                      return next;
                    })
                  }
                  onToggleSelect={toggleSelect}
                  onCommit={commit}
                  onOpen={(id) => navigate(`/travel/accounting/accounts/${id}`)}
                  onAddChild={(node) => {
                    setCreateUnder(node);
                    setModalOpen(true);
                  }}
                  onDrop={(draggedId, targetId) => {
                    const dragged = flat.find((n) => n.id === draggedId);
                    const target = flat.find((n) => n.id === targetId);
                    if (!dragged || !target) return;
                    // Dropping a branch inside itself would detach it from the
                    // tree; the server refuses it too, but say so before the
                    // dialog rather than after.
                    if (isInvalidDrop(dragged, target)) {
                      setRowWarnings([t('reparent.invalidDrop')]);
                      return;
                    }
                    setPendingDrop({ dragged, target });
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AccountModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        parentNode={createUnder ? { id: createUnder.id, code: createUnder.code, type: createUnder.type } : undefined}
      />
      <TravelExportCoaDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        visibleTree={visible as never}
        visibleCount={flat.length}
      />
      <ImportAccountsDialog open={importOpen} onOpenChange={setImportOpen} />
      {pendingDrop && (
        <ReparentDialog
          dragged={pendingDrop.dragged}
          target={pendingDrop.target}
          onClose={() => setPendingDrop(null)}
        />
      )}
    </div>
  );
}
